import { AssetsImages, TWidgetCommonOption, TMargin, TViewCommonOption } from '@libs/types';
import { CoreImageView, CoreLayoutView, ORIENTATION_ENUM, TCoreLayoutOptions, type TCoreLayoutStyleProps } from '@core/widgets';
import clsx from 'clsx';
import React, { MouseEventHandler } from 'react';
import { TVisualOption } from '../../types/options';
import { Badge } from '../UI/Badge/Badge';
import { FlagIcon } from '../UI/FlagIcon/FlagIcon';
import { getHref } from './imageUtils';
import styles from './styles/visual.module.css';

export type TVisualViewProps = {
    uid?: string;
    iconName?: string;
    defaultIconName?: string;
    iconVariant?: 'simple' | 'circle';
    iconHierarchy?: TVisualOption['iconHierarchy'];
    size?: TVisualOption['size'];
    flagIcon?: string;
    flagIconSmall?: string;
    avatar?: string;
    base64?: string;
    rightMargin?: boolean;
    leftMargin?: boolean;
    margin?: TMargin;
    isSelected?: boolean;
    handleClick?: MouseEventHandler;
} & Pick<TViewCommonOption, 'isDisabled' | 'isHide' | 'parentStyleNames' | 'styleName'> &
    Pick<TCoreLayoutOptions, 'opacity'>;

const getFlagSize = (size?: TVisualViewProps['size']) => {
    return size === 'medium' ? '80px' : '40px';
};

const getIconSize = <T,>(size?: T) => {
    switch (size) {
        case 'xxSmall':
            return 20;
        case 'medium':
            return 48;
        case 'large':
            return 80;
        default:
            return 24;
    }
};

const getIconWrapperSize = <T,>(size?: T) => {
    switch (size) {
        case 'xxSmall':
            return 20;
        case 'medium':
            return 80;
        case 'large':
            return 100;
        default:
            return 40;
    }
};

export const getIconColor = (disabled: boolean, iconHierarchy?: TVisualOption['iconHierarchy'], iconVariant?: TVisualOption['iconVariant']) => {
    switch (iconHierarchy) {
        case 'highlighted':
            return 'white_base';
        case 'primary': {
            if (iconVariant === 'circle') {
                return disabled ? 'purple_40_tr' : 'purple_100';
            }
            return disabled ? 'grey_40_lc' : 'black';
        }
        case 'emphasised':
            return 'white_base';
        case 'secondary': {
            if (iconVariant === 'circle') {
                return disabled ? 'grey_40_lc' : 'black';
            }
            return disabled ? 'grey_40_lc' : 'grey_72_lc';
        }
        case 'secondaryAlt':
            return disabled ? 'grey_40_lc' : 'black';
        case 'destructive':
            return disabled ? 'pink_40_lc' : 'pink_100';
        default:
            return disabled ? 'purple_40_tr' : 'purple_100';
    }
};

const getBase64 = (base64?: string) => {
    if (base64) {
        return base64.startsWith('data:image') ? base64 : 'data:image;base64,' + base64;
    }
    return undefined;
};

/**
 * Composable building block for rendering a visual element (icon, avatar, flag icon).
 * Owns only the rendering output; accepts pre-resolved behavior props (isDisabled, isHide)
 * from the parent, making it suitable for use inside complex widget trees where behavior
 * state is already managed one level up.
 *
 * Does not call useBehavior or any other behavior hook — those are the responsibility
 * of VisualWidget (server-driven) or the direct caller (composable usage).
 */
export const VisualView = ({
    uid,
    iconName,
    defaultIconName,
    iconVariant,
    iconHierarchy,
    size,
    flagIcon,
    flagIconSmall,
    avatar,
    base64,
    rightMargin,
    margin,
    isDisabled = false,
    isHide,
    isSelected,
    handleClick,
    styleName,
    parentStyleNames,
    opacity,
}: TVisualViewProps) => {
    const base64ForImage = getBase64(base64);
    const iconImage = AssetsImages[iconName as keyof typeof AssetsImages];

    const containerMargin = rightMargin ? { ...(margin ?? {}), right: 16 } : margin;

    const getContainerSize = () => {
        if (base64ForImage || avatar) {
            const s = size === 'medium' ? 80 : 40;
            return { width: s, height: s };
        }
        if (iconName) {
            return {
                width: iconImage?.width ?? getIconWrapperSize(size),
                height: iconImage?.height ?? getIconWrapperSize(size),
            };
        }
        if (flagIcon) {
            const s = size === 'medium' ? 80 : 40;
            return { width: s, height: s };
        }
        return { width: 'auto', height: 'auto' };
    };

    const { width: containerWidth, height: containerHeight } = getContainerSize();

    const circleBackgroundColor = (() => {
        if (iconVariant !== 'circle') return undefined;
        if (isDisabled && iconHierarchy !== 'emphasised') return undefined;
        switch (iconHierarchy) {
            case 'emphasised':
                return isDisabled ? 'var(--color-purple_8_tr)' : 'var(--color-purple_100)';
            case 'secondary':
                return 'var(--color-grey_8_lc)';
            case 'secondaryAlt':
                return 'var(--color-white)';
            case 'destructive':
                return 'var(--color-pink_8_tr)';
            default:
                return 'var(--color-purple_8_tr)';
        }
    })();

    const circleBorderColor = (() => {
        if (iconVariant !== 'circle' || iconHierarchy === 'emphasised') return undefined;
        if (isDisabled) {
            switch (iconHierarchy) {
                case 'secondary':
                    return 'var(--color-grey_8_lc)';
                case 'destructive':
                    return 'var(--color-pink_8_lc)';
                default:
                    return 'var(--color-purple_16_tr)';
            }
        }
        return undefined;
    })();

    const containerStyleProps: TCoreLayoutStyleProps = {
        orientation: ORIENTATION_ENUM.BOX,
        borderRadius: 1000,
        margin: containerMargin,
        width: containerWidth,
        flexShrink: 0,
        height: containerHeight,
        backgroundColor: circleBackgroundColor,
        borderColor: circleBorderColor,
        borderStroke: iconVariant === 'circle' ? 1 : undefined,
        opacity,
    };

    const visualImage = 'visualImage';
    const visualLayout = 'visualLayout';

    const getVisual = () => {
        if (base64ForImage || avatar) {
            const avatarSize = size === 'medium' ? 80 : 40;
            return (
                <CoreImageView
                    src={base64ForImage || avatar}
                    isDisabled={isDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName={visualImage}
                    defaultIcon={getHref(defaultIconName ?? '')}
                    styleProps={{
                        width: avatarSize,
                        height: avatarSize,
                        defaultIconHeight: 24,
                        defaultIconWidth: 24,
                        borderRadius: 1000,
                        overflow: 'hidden',
                        iconColor: `var(--color-${getIconColor(isDisabled, iconHierarchy, iconVariant)})`,
                        objectFit: 'cover',
                    }}
                />
            );
        } else if (iconName || defaultIconName) {
            return (
                <CoreImageView
                    src={getHref(iconName)}
                    isDisabled={isDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName={visualImage}
                    defaultIcon={getHref(defaultIconName)}
                    styleProps={{
                        width: iconImage?.width ?? getIconSize(size),
                        height: iconImage?.height ?? getIconSize(size),
                        iconColor: `var(--color-${getIconColor(isDisabled, iconHierarchy, iconVariant)})`,
                    }}
                />
            );
        } else if (flagIcon) {
            return <FlagIcon name={flagIcon} size={getFlagSize(size)} isDisabled={isDisabled} />;
        }
    };

    return (
        <CoreLayoutView
            uid={uid}
            role="img"
            isHide={isHide}
            isDisabled={isDisabled}
            styleProps={containerStyleProps}
            parentStyleNames={parentStyleNames}
            styleName={visualLayout}
            onClick={handleClick}
        >
            {getVisual()}
            {flagIconSmall && (
                <span className={clsx(styles.badgeContainer, size === 'medium' && styles.medium)}>
                    <Badge flagIcon={flagIconSmall} size={size} parentStyleNames={parentStyleNames} styleName={styleName} isDisabled={isDisabled} />
                </span>
            )}
            {isSelected && (
                <div className={styles.selectedContainer}>
                    <CoreImageView
                        src={getHref('ic_check')}
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName={visualImage}
                        styleProps={{ width: 24, height: 24, iconColor: 'var(--color-purple_100)' }}
                    />
                </div>
            )}
        </CoreLayoutView>
    );
};
